'use client'
import '../styles/globals.css'
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { initializeApp } from 'firebase/app'
import { getAuth, onAuthStateChanged, User } from 'firebase/auth'
import { QueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/nextjs'

import AuthContext from '../context/AuthContext'
import { getMemorizedCompanyId, setMemorizedCompanyId, clearMemorizedCompanyId } from '../services/auth'
import { getFirebaseConfig } from './GetConfig'
import Loader from '../components/Loader/Loader'
import { useRouter } from 'next/navigation'
import useUser from '../hooks/queries/useUser'
import useCompanies, { Company } from '../hooks/queries/useCompanies'
import useCompanyManager from '../hooks/queries/useCompanyManager'
import { toast } from 'sonner'

const queryClient = new QueryClient()

function ClientLayout({ children }: PropsWithChildren) {
	const [loggedIn, setLoggedIn] = useState(false)
	const [authLoading, setAuthLoading] = useState(true)
	const [firebaseUser, setFirebaseUser] = useState<User | null>(null)
	const [company, setCompany] = useState<Company | null>(null)
	const router = useRouter()

	const { user, isLoading: userLoading, error: userError } = useUser(!!firebaseUser)
	const {
		companies,
		isLoading: companiesLoading,
		error: companiesError,
		refetch: refetchCompanies,
	} = useCompanies(!!user)
	const { isManager: isCompanyManager, isLoading: isManagerLoading } = useCompanyManager(company?.id)

	useEffect(() => {
		if (userError) {
			Sentry.addBreadcrumb({
				category: 'api',
				message: 'User data loading failed',
				level: 'error',
				data: { error: userError.message },
			})
			Sentry.setContext('app_state', {
				hasFirebaseUser: !!firebaseUser,
				firebaseUserId: firebaseUser?.uid,
				authLoading,
				loggedIn,
			})
			Sentry.captureException(userError)
		}
	}, [userError, firebaseUser, authLoading, loggedIn])

	useEffect(() => {
		if (companiesError) {
			Sentry.addBreadcrumb({
				category: 'api',
				message: 'Companies data loading failed',
				level: 'error',
				data: { error: companiesError.message },
			})
			Sentry.setContext('app_state', {
				hasUser: !!user,
				userId: user?.id,
				hasFirebaseUser: !!firebaseUser,
				companiesCount: companies?.length || 0,
				hasSelectedCompany: !!company,
			})
			Sentry.captureException(companiesError)
		}
	}, [companiesError, user, firebaseUser, companies, company])

	const handleSetCompany = useCallback(
		async (newCompany: Company, shouldNavigate: boolean = false) => {
			if (!newCompany?.id) {
				return
			}

			// Immediately update the company state for instant UI feedback
			setCompany(newCompany)

			// Memorize current company for reload
			setMemorizedCompanyId(newCompany.id)

			if (shouldNavigate) {
				try {
					// Clear all local cache after changing company.
					await queryClient.invalidateQueries()
					await queryClient.refetchQueries()
					const fireBaseUser = getAuth().currentUser
					if (fireBaseUser) {
						// Refetch companies data
						refetchCompanies()
					}

					router.push('/dashboard/members')
				} catch (error) {
					Sentry.addBreadcrumb({
						message: 'Error during company switch',
						level: 'error',
						data: {
							category: 'company',
							companyId: newCompany.id,
							error: error instanceof Error ? error.message : String(error),
						},
					})
					Sentry.captureException(error)
				}
			}
		},
		[refetchCompanies, router],
	)

	const selectCompany = useCallback(
		async (allCompanies: Company[]) => {
			const memorizedCompanyId = getMemorizedCompanyId()

			let selectedCompany = allCompanies[0]
			if (memorizedCompanyId) {
				const memorizedCompany = allCompanies.find((company: Company) => company.id === memorizedCompanyId)
				if (memorizedCompany) {
					selectedCompany = memorizedCompany
				} else {
					clearMemorizedCompanyId()
				}
			}

			if (memorizedCompanyId !== selectedCompany.id) {
				setMemorizedCompanyId(selectedCompany.id)
			}

			await handleSetCompany(selectedCompany, false) // No navigation for automatic selection
			return selectedCompany
		},
		[handleSetCompany],
	)

	useEffect(() => {
		Promise.all([getFirebaseConfig()])
			.then(([firebaseConfig]) => {
				if (firebaseConfig.apiKey) {
					initializeApp(firebaseConfig)
					const auth = getAuth()

					onAuthStateChanged(auth, async firebaseuser => {
						Sentry.addBreadcrumb({
							category: 'auth',
							message: firebaseuser ? 'User authenticated' : 'User logged out',
							level: 'info',
							data: {
								userId: firebaseuser?.uid,
								hasUser: !!firebaseuser,
							},
						})

						if (firebaseuser) {
							Sentry.setUser({ id: firebaseuser.uid })
							setFirebaseUser(firebaseuser)
							setLoggedIn(true)
						} else {
							clearMemorizedCompanyId()
							queryClient.clear()
							Sentry.setUser(null)
							setFirebaseUser(null)
							setLoggedIn(false)
						}
						setAuthLoading(false)
					})
				} else {
					Sentry.addBreadcrumb({
						category: 'auth',
						message: 'Firebase configuration invalid - missing API key',
						level: 'error',
						data: {
							firebaseConfig,
						},
					})
					setAuthLoading(false)
					toast.success('Firebase configuration is missing or invalid', {
						description: 'Please contact support',
					})
				}
			})
			.catch(error => {
				Sentry.addBreadcrumb({
					category: 'auth',
					message: 'Firebase config loading failed',
					level: 'error',
					data: { errorMessage: error.message },
				})
				Sentry.captureException(error)
				setAuthLoading(false)
				toast.error('Failed to load Firebase configuration', {
					description: error.message,
				})
			})
	}, [])

	// Handle user data loading and companies loading
	useEffect(() => {
		if (firebaseUser && user && !userLoading && companies && !companiesLoading) {
			// If current company is no longer in the companies list (e.g., user was removed), clear it
			if (company && !companies.find(c => c.id === company.id)) {
				const memorizedCompanyId = getMemorizedCompanyId()
				if (memorizedCompanyId !== company.id) {
					// Company was removed and it's not the memorized one, clear it
					setCompany(null)
					clearMemorizedCompanyId()
				} else {
					// Company is memorized but not in list yet, keep it (probably just created)
					return
				}
			}

			// If we already have a company selected and it's still valid, don't re-select
			if (company && companies.find(c => c.id === company.id)) {
				return
			}

			if (!company && companies.length > 0) {
				selectCompany(companies).catch(error => {
					Sentry.captureException(error)
				})
			}
		}
	}, [firebaseUser, user, userLoading, companies, companiesLoading, company, selectCompany])

	// Show loading until ALL auth data is ready
	const showLoading = useMemo(() => {
		if (authLoading || !loggedIn) return authLoading
		if (firebaseUser && userLoading) return true
		if (firebaseUser && user === null && !userLoading) return false
		return !!user && (!company || companiesLoading || isManagerLoading)
	}, [authLoading, loggedIn, firebaseUser, user, userLoading, companiesLoading, isManagerLoading, company])

	return showLoading ? (
		<div className="flex items-center justify-center h-screen">
			<Loader />
		</div>
	) : (
		<AuthContext.Provider
			value={{
				loggedIn,
				loading: showLoading,
				user: user || null,
				company,
				setCompanyName: newName => {
					setCompany(prevCompany => {
						if (prevCompany) {
							return { ...prevCompany, name: newName }
						}
						return prevCompany
					})
				},
				setCompany: async newCompany => {
					await handleSetCompany(newCompany, true) // Navigate after manual company switch
				},
				companies,
				isCompanyManager,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export default ClientLayout
